import { useMemo, useState } from 'react'
import Axios from 'axios'
import useSWR from 'swr'
import {
  Button,
  CircularProgress,
  ClickAwayListener,
  IconButton,
  Tooltip,
  Typography,
} from '@material-ui/core'
import { InfoOutlined } from '@material-ui/icons'
import { makeStyles } from '@material-ui/core/styles'
import { ApexOptions } from 'apexcharts'
import jalali from 'jalaali-js'

import { ApexChart } from 'ApexChart'
import { strings } from 'strings'
import { store } from 'store/store'

import { BucketDetailsDialog } from './BucketDetailsDialog'
import {
  BucketGranularity,
  BucketSpec,
  formatHourLabel,
  useAnalyticsRange,
} from './analyticsShared'

const PERSIAN_WEEKDAYS = [
  'یکشنبه',
  'دوشنبه',
  'سه‌شنبه',
  'چهارشنبه',
  'پنج‌شنبه',
  'جمعه',
  'شنبه',
]

const PERSIAN_MONTHS = [
  'فروردین',
  'اردیبهشت',
  'خرداد',
  'تیر',
  'مرداد',
  'شهریور',
  'مهر',
  'آبان',
  'آذر',
  'دی',
  'بهمن',
  'اسفند',
]

const PERSIAN_DIGITS = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹']

function toPersianDigits(s: string | number): string {
  return String(s).replace(/\d/g, (d) => PERSIAN_DIGITS[Number(d)])
}

type MetricKey =
  | 'bases'
  | 'rovers'
  | 'devices'
  | 'raymandRovers'
  | 'nonRaymandRovers'

type ListKind = 'bases' | 'rovers' | 'devices'

const EPOCH_ISO = '2000-01-01T00:00:00Z'

interface SeriesSpec {
  key: MetricKey
  name: string
  color: string
}

interface ListButtonSpec {
  label: string
  title: string
  kind: ListKind
  raymand?: boolean
  csvFilename: string
}

interface ActivityPoint {
  date: string
  bases: number
  rovers: number
  devices: number
  raymandRovers: number
  nonRaymandRovers: number
}

interface ActivityResponse {
  timezone?: string
  points: ActivityPoint[]
}

function authHeader() {
  const token =
    store.getState().auth.token ||
    localStorage.getItem('token') ||
    sessionStorage.getItem('token')
  return { Authorization: `Bearer ${token}` }
}

async function fetchActivity(
  endpoint: string,
  from: string,
  to: string,
  granularity: BucketGranularity,
): Promise<ActivityResponse> {
  const params: Record<string, string> = { from, to }
  if (granularity === 'hour') params.granularity = 'hour'
  const res = await Axios.get(endpoint, {
    params,
    headers: authHeader(),
  })
  const data = res.data as Partial<ActivityResponse> | null
  return {
    timezone: data?.timezone,
    points: data?.points ?? [],
  }
}


// "<Persian weekday> <Jalali day> <Jalali month>", e.g. "پنج‌شنبه ۲ اردیبهشت".
function formatDayLabel(dayKey: string): string {
  const [y, m, d] = dayKey.split('-').map(Number)
  const dt = new Date(Date.UTC(y, m - 1, d))
  const weekday = PERSIAN_WEEKDAYS[dt.getUTCDay()]
  const j = jalali.toJalaali(y, m, d)
  const month = PERSIAN_MONTHS[j.jm - 1]
  return `${weekday} ${toPersianDigits(j.jd)} ${month}`
}

interface FilledPoint {
  key: string
  bases: number
  rovers: number
  devices: number
  raymandRovers: number
  nonRaymandRovers: number
}

function fillBuckets(
  points: ActivityPoint[],
  spec: BucketSpec,
): FilledPoint[] {
  const index = new Map<string, ActivityPoint>()
  for (const p of points) index.set(p.date, p)
  return spec.keys.map((k) => {
    const p = index.get(k)
    return {
      key: k,
      bases: p?.bases ?? 0,
      rovers: p?.rovers ?? 0,
      devices: p?.devices ?? 0,
      raymandRovers: p?.raymandRovers ?? 0,
      nonRaymandRovers: p?.nonRaymandRovers ?? 0,
    }
  })
}

function buildOptions(
  points: FilledPoint[],
  series: SeriesSpec[],
  categories: string[],
  onMarkerClick: (dayKey: string, kind: MetricKey) => void,
): ApexOptions {
  const discreteMarkers: Array<{
    seriesIndex: number
    dataPointIndex: number
    fillColor: string
    strokeColor: string
    size: number
  }> = []
  series.forEach((s, si) => {
    points.forEach((p, pi) => {
      if (p[s.key] > 0) {
        discreteMarkers.push({
          seriesIndex: si,
          dataPointIndex: pi,
          fillColor: s.color,
          strokeColor: '#fff',
          size: 5,
        })
      }
    })
  })

  const multi = series.length > 1

  return {
    series: series.map((s) => ({
      name: s.name,
      data: points.map((p) => p[s.key]),
    })),
    chart: {
      type: 'line',
      fontFamily: 'vazir',
      height: 360,
      animations: { enabled: false },
      toolbar: { show: true },
      events: {
        markerClick: (
          _e,
          _ctx,
          cfg: { seriesIndex?: number; dataPointIndex?: number },
        ) => {
          const si = cfg?.seriesIndex ?? 0
          const pi = cfg?.dataPointIndex ?? -1
          const s = series[si]
          const p = points[pi]
          if (s && p && p[s.key] > 0) onMarkerClick(p.key, s.key)
        },
      },
    },
    stroke: { curve: 'straight', width: 3 },
    markers: { size: 0, discrete: discreteMarkers, hover: { size: 6 } },
    dataLabels: {
      enabled: !multi,
      enabledOnSeries: [0],
      formatter: (_val, opts) => {
        const idx = (opts as { dataPointIndex?: number })?.dataPointIndex ?? -1
        const p = points[idx]
        const s = series[0]
        if (!p || !s || p[s.key] <= 0) return ''
        return toPersianDigits(p[s.key])
      },
      offsetY: -8,
      style: { fontSize: '11px', colors: [series[0]?.color ?? '#000'] },
      background: { enabled: false },
    },
    xaxis: {
      categories,
      labels: { rotate: -45, style: { fontSize: '11px' } },
    },
    yaxis: {
      min: 0,
      title: { text: strings.activeCount },
      labels: {
        formatter: (val) => String(Math.round(val as number)),
      },
    },
    legend: { position: 'top', horizontalAlign: 'left' },
    colors: series.map((s) => s.color),
    tooltip: {
      y: { formatter: (val) => String(Math.round(val as number)) },
    },
  }
}

interface ActivityLineCardProps {
  title: string
  listKind: ListKind
  series: SeriesSpec[]
  listButtons: ListButtonSpec[]
  hint?: string
}

interface SelectedMarker {
  day: string
  kind: MetricKey
}

function ActivityLineCard({
  title,
  listKind,
  series,
  listButtons,
  hint,
}: ActivityLineCardProps) {
  const classes = useStyles()
  const { bucketSpec: spec } = useAnalyticsRange()
  const [selected, setSelected] = useState<SelectedMarker | null>(null)
  const [listOpen, setListOpen] = useState<number | null>(null)
  const [hintOpen, setHintOpen] = useState(false)
  const allListTo = useMemo(() => new Date().toISOString(), [listOpen])

  const { data, error } = useSWR<ActivityResponse>(
    ['/auto-stats/activity', spec.from, spec.to, spec.granularity],
    fetchActivity,
  )

  const points = useMemo(() => {
    if (!data) return null
    return fillBuckets(data.points, spec)
  }, [data, spec])

  const loading = !error && !data
  const isEmpty = !loading && (data?.points?.length ?? 0) === 0

  const categories = useMemo(
    () =>
      spec.keys.map((k) =>
        spec.granularity === 'hour' ? formatHourLabel(k) : formatDayLabel(k),
      ),
    [spec],
  )

  const options: ApexOptions | null = useMemo(() => {
    if (!points || isEmpty || series.length === 0) return null
    return buildOptions(points, series, categories, (day, kind) =>
      setSelected({ day, kind }),
    )
  }, [points, isEmpty, series, categories])

  const selectedSeriesName = selected
    ? series.find((s) => s.key === selected.kind)?.name ?? title
    : title

  const selectedListKind: ListKind =
    selected?.kind === 'raymandRovers' || selected?.kind === 'nonRaymandRovers'
      ? 'rovers'
      : selected
        ? (selected.kind as ListKind)
        : listKind
  const selectedRaymand: boolean | undefined =
    selected?.kind === 'raymandRovers'
      ? true
      : selected?.kind === 'nonRaymandRovers'
        ? false
        : undefined

  const activeListButton = listOpen !== null ? listButtons[listOpen] : null

  return (
    <div className={classes.card}>
      <div className={classes.cardHeader}>
        <Typography className={classes.title}>{title}</Typography>
        <div className={classes.controls}>
          {listButtons.map((b, i) => (
            <Button
              key={b.label}
              size="small"
              variant="outlined"
              onClick={() => setListOpen(i)}
            >
              {b.label}
            </Button>
          ))}
          {hint && (
            <ClickAwayListener onClickAway={() => setHintOpen(false)}>
              <Tooltip
                open={hintOpen}
                onClose={() => setHintOpen(false)}
                disableFocusListener
                disableHoverListener
                disableTouchListener
                interactive
                placement="bottom-end"
                arrow
                PopperProps={{ disablePortal: true }}
                classes={{ tooltip: classes.hintTooltip }}
                title={<div className={classes.hint}>{hint}</div>}
              >
                <IconButton
                  size="small"
                  onClick={() => setHintOpen((v) => !v)}
                  aria-label="hint"
                >
                  <InfoOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
            </ClickAwayListener>
          )}
        </div>
      </div>
      <div className={classes.chartWrap}>
        {error && (
          <Typography className={classes.status}>
            {strings.error}: {String((error as any)?.message ?? error)}
          </Typography>
        )}
        {!error && loading && (
          <div className={classes.status}>
            <CircularProgress size={24} />
            <Typography>{strings.loading}</Typography>
          </div>
        )}
        {!error && !loading && isEmpty && (
          <Typography className={classes.status}>
            {strings.noActivityInRange}
          </Typography>
        )}
        {!error && options && (
          <ApexChart options={options} className={classes.chart} />
        )}
      </div>
      <BucketDetailsDialog
        open={selected !== null}
        onClose={() => setSelected(null)}
        title={
          selected
            ? `${selectedSeriesName} — ${
                spec.granularity === 'hour'
                  ? `${formatDayLabel(selected.day.slice(0, 10))} ${formatHourLabel(selected.day)}`
                  : formatDayLabel(selected.day)
              }`
            : title
        }
        bucket={selected?.day ?? null}
        granularity={spec.granularity}
        kind={selectedListKind}
        mode="activity"
        raymand={selectedRaymand}
      />
      <BucketDetailsDialog
        open={activeListButton !== null}
        onClose={() => setListOpen(null)}
        title={activeListButton?.title ?? ''}
        kind={activeListButton?.kind ?? listKind}
        from={EPOCH_ISO}
        to={allListTo}
        csvFilename={activeListButton?.csvFilename ?? listKind}
        raymand={activeListButton?.raymand}
      />
    </div>
  )
}

interface CardSpec {
  id: string
  title: string
  listKind: ListKind
  series: SeriesSpec[]
  listButtons: ListButtonSpec[]
  hint?: string
}

const CARDS: CardSpec[] = [
  {
    id: 'bases-devices',
    title: strings.activeBasesAndDevices,
    listKind: 'bases',
    series: [
      { key: 'bases', name: strings.activeBases, color: '#1976d2' },
      { key: 'devices', name: strings.activeDevices, color: '#198D10' },
    ],
    listButtons: [
      {
        label: strings.showAllBases,
        title: strings.allBases,
        kind: 'bases',
        csvFilename: 'all-bases',
      },
      {
        label: strings.showAllDevices,
        title: strings.allDevices,
        kind: 'devices',
        csvFilename: 'all-devices',
      },
    ],
  },
  {
    id: 'rovers',
    title: strings.activeRovers,
    listKind: 'rovers',
    hint: strings.roverActivityHint,
    series: [
      {
        key: 'rovers',
        name: strings.activeRovers,
        color: '#7b1fa2',
      },
      {
        key: 'raymandRovers',
        name: strings.activeRaymandRovers,
        color: '#2e7d32',
      },
      {
        key: 'nonRaymandRovers',
        name: strings.activeNonRaymandRovers,
        color: '#c62828',
      },
    ],
    listButtons: [
      {
        label: strings.showAllRovers,
        title: strings.allRovers,
        kind: 'rovers',
        csvFilename: 'all-rovers',
      },
      {
        label: strings.showAllRaymandRovers,
        title: strings.allRaymandRovers,
        kind: 'rovers',
        raymand: true,
        csvFilename: 'all-raymand-rovers',
      },
      {
        label: strings.showAllNonRaymandRovers,
        title: strings.allNonRaymandRovers,
        kind: 'rovers',
        raymand: false,
        csvFilename: 'all-non-raymand-rovers',
      },
    ],
  },
]

export function ActivityChart() {
  return (
    <>
      {CARDS.map((c) => (
        <ActivityLineCard
          key={c.id}
          title={c.title}
          listKind={c.listKind}
          series={c.series}
          listButtons={c.listButtons}
          hint={c.hint}
        />
      ))}
    </>
  )
}

const useStyles = makeStyles((theme) => ({
  card: {
    border: '1px solid #e0e0e0',
    borderRadius: 4,
    overflow: 'hidden',
    width: '100%',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 1),
    background: 'silver',
    flexWrap: 'wrap',
  },
  title: {
    fontWeight: 600,
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  chartWrap: {
    width: '100%',
    height: 360,
  },
  chart: {
    width: '100%',
    height: '100%',
  },
  status: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
    height: 360,
    justifyContent: 'center',
  },
  hint: {
    flip: false,
    fontSize: '0.8rem',
    lineHeight: 1.8,
    whiteSpace: 'pre-line',
    direction: 'rtl',
    textAlign: 'right',
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.72rem',
      lineHeight: 1.6,
    },
  },
  hintTooltip: {
    flip: false,
    maxWidth: 420,
    padding: theme.spacing(1.25, 1.5),
    fontSize: '0.8rem',
    direction: 'rtl',
    textAlign: 'right',
  },
}))
